/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';

// Sagas
import { takeLatest, call, all, put, select } from 'redux-saga/effects';

// Connected Router
import { push } from 'connected-react-router';

// Components
import { ModalBulkErrors } from 'components/ModalBulkErrors';

// Services
import { collaboratorsApi } from 'services/apiDigitalTicket/collaborators';
import { appUser } from 'services/apiDigitalTicket/users';
import { auth } from 'services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import ENVS from 'constants/enviroments';
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';
import { ERROR_CODE_MAP } from 'constants/errorCodes';

// Helpers
import { findDocumentByType } from 'helpers/document';

// Store
import store from 'store';

// Actions
import * as CollaboratorsActions from './actions';

// Types
import { CollaboratorsTypes, CollaboratorInterface } from './types';

function* list({
  payload,
}: ReturnType<typeof CollaboratorsActions.listCollaboratorsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando colaboradores' }),
      );
    }
    const response = yield call(collaboratorsApi.get, {
      params: { ...body },
    });

    if (response.status === 200) {
      yield put(
        CollaboratorsActions.listCollaboratorsSuccess({
          collaboratorsList: response.data.map(
            (collaborators: CollaboratorInterface) => ({
              ...collaborators,
              document: findDocumentByType(collaborators?.UserDocuments),
            }),
          ),
          totalCount: response.headers.totalcount
            ? parseInt(response.headers.totalcount, 10)
            : 0,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - listCollaborators - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.listCollaboratorsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof CollaboratorsActions.getCollaboratorRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(appUser.get, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        CollaboratorsActions.getCollaboratorSuccess({
          collaboratorData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - getCollaborator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.getCollaboratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof CollaboratorsActions.filterCollaboratorsRequest>): any {
  try {
    const response = yield call(collaboratorsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        CollaboratorsActions.filterCollaboratorsSuccess({
          collaboratorsList: response.data.map(
            (collaborators: CollaboratorInterface) => ({
              ...collaborators,
              document: findDocumentByType(collaborators?.UserDocuments),
            }),
          ),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - filterCollaborators - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.filterCollaboratorsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof CollaboratorsActions.createCollaboratorRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando colaborador' }),
    );
    const response = yield call(collaboratorsApi.create, {
      data: [
        {
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          document: payload.document,
        },
      ],
      companyId: payload.companyId,
    });

    if (response.status === 200) {
      if (response.data.error.length > 0) {
        const message = errorMessagersList(response.data.error);

        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'error',
            message,
          }),
        );
        yield put(DialogsActions.setClose());
      } else if (payload.email) {

        yield call(() =>
          sendPasswordResetEmail(auth, response.data.success[0].email, {
            url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
          }),
        );

        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message:
              'Colaborador cadastrado com sucesso. E-mail de definição de senha enviado.',
          }),
        );
        yield put(DialogsActions.setClose());
        yield put(CollaboratorsActions.createCollaboratorSuccess());
        if (!payload.companyId) {
          yield put(push(PATH.COMPANY_COLLABORATORS.BASE_URL));
        } else {
          yield put(
            push(`${PATH.COMPANIES.BASE_URL}/editar/${payload.companyId}`),
          );
        }
      } else {
        yield put(DialogsActions.setClose());
        yield put(CollaboratorsActions.createCollaboratorSuccess());
        if (!payload.companyId) {
          yield put(push(PATH.COMPANY_COLLABORATORS.BASE_URL));
        } else {
          yield put(
            push(`${PATH.COMPANIES.BASE_URL}/editar/${payload.companyId}`),
          );
        }
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - createCollaborator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.createCollaboratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* linkUserToCompany({
  payload,
}: ReturnType<
  typeof CollaboratorsActions.linkCollaboratorToCompanyRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Vinculando colaborador à empresa',
      }),
    );
    const response = yield call(collaboratorsApi.linkUserToCompany, {
      users: payload.users,
      authUserId: payload.authUserId,
    });

    if (response.status === 200) {
      yield put(
        CollaboratorsActions.linkCollaboratorToCompanySuccess({
          linkCollaboratorCompanyData: response.data,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Colaborador vinculado à empresa',
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - linkUserToCompany - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.linkCollaboratorToCompanyFailure());
    yield put(DialogsActions.setClose());
  }
}

function* unlinkUserFromCompany({
  payload,
}: ReturnType<
  typeof CollaboratorsActions.unlinkCollaboratorFromCompanyRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Desvinculando colaborador da empresa',
      }),
    );
    const response = yield call(collaboratorsApi.unlinkUserFromCompany, {
      users: payload.users,
      authUserId: payload.authUserId,
    });

    if (response.status === 200) {
      yield put(
        CollaboratorsActions.unlinkCollaboratorFromCompanySuccess({
          unlinkCollaboratorCompanyData: response.data,
        }),
      );
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Colaborador desvinculado da empresa',
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - unlinkUserFromCompany - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.unlinkCollaboratorFromCompanyFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof CollaboratorsActions.updateCollaboratorRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando colaborador' }));
    const userFirebaseData = yield select((state) => state.users.firebaseData);
    const response = yield call(collaboratorsApi.update, {
      data: { users: [{ ...payload }] },
    });

    if (response.status === 200) {
      yield put(CollaboratorsActions.updateCollaboratorSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const message = errorMessagersList(response.data.error);

        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'error',
            message,
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Colaborador alterado com sucesso',
          }),
        );
        yield put(
          CollaboratorsActions.listCollaboratorsRequest({
            body: {
              companyId: userFirebaseData.uid,
              _includes: 'transportcard,document',
            },
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - updateCollaborator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.updateCollaboratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof CollaboratorsActions.deleteCollaboratorRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo colaborador' }),
    );
    const userFirebaseData = yield select((state) => state.users.firebaseData);

    const response = yield call(collaboratorsApi.delete, {
      data: {
        users: Array.isArray(payload.id)
          ? payload.id?.map((id) => ({ id }))
          : [{ ...payload }],
      },
    });

    if (response.status === 200) {
      yield put(CollaboratorsActions.deleteCollaboratorSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Colaborador excluído com sucesso',
        }),
      );
      yield put(
        CollaboratorsActions.listCollaboratorsRequest({
          body: {
            companyId: userFirebaseData.uid,
            _includes: 'transportcard,document',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - deleteCollaborator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.deleteCollaboratorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
}: ReturnType<typeof CollaboratorsActions.createCollaboratorsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando colaboradores' }),
    );
    const userFirebaseData = yield select((state) => state.users.firebaseData);
    const response = yield call(collaboratorsApi.create, { data: payload });

    if (response.status === 200) {
      yield put(CollaboratorsActions.createCollaboratorsSuccess());

      if (response.data.success && response.data.success.length > 0) {
        yield all(
          response.data.success.map(({ email }: any) =>
            call(() =>
              sendPasswordResetEmail(auth, email, {
                url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
              }),
            ),
          ),
        );

        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: `${response.data.success.length} Colaborador(es) cadastrado(s) com sucesso. E-mail(s) de definição de senha enviado(s).`,
          }),
        );
      }

      if (response.data.error && response.data.error.length > 0) {
        yield put(DialogsActions.setClose());

        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.flatMap((element: any) => {
            const errorStr: string = element.error || '';

            // Multiple errors joined by ",\n"
            if (errorStr.includes(',\n')) {
              const codes = errorStr
                .split(/,\s*\n/)
                .map((s) => s.replace(/^Validation error:\s*/i, '').trim())
                .filter(Boolean);
              return [...new Set(codes)].map((code) => {
                const mapped =
                  ERROR_CODE_MAP.get(code) ||
                  ERROR_CODE_MAP.get(code.split(':')[0].trim());
                return {
                  primaryText: element.name,
                  secondaryText: mapped ? mapped.message : errorMessagersList(code),
                };
              });
            }

            // Single error
            const mappedError =
              ERROR_CODE_MAP.get(errorStr) ||
              ERROR_CODE_MAP.get(errorStr.split(':')[0].trim());
            return [{
              primaryText: element.name,
              secondaryText: mappedError ? mappedError.message : errorMessagersList(errorStr),
            }];
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(
                CollaboratorsActions.listCollaboratorsRequest({
                  body: {
                    companyId: userFirebaseData.uid,
                    _includes: 'transportcard,document',
                  },
                }),
              );
            },
          }),
        );
      } else {
        yield put(DialogsActions.setClose());

        yield put(
          CollaboratorsActions.listCollaboratorsRequest({
            body: {
              companyId: userFirebaseData.uid,
              _includes: 'transportcard,document',
            },
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - createCollaborators - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.createCollaboratorsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateList({
  payload,
}: ReturnType<typeof CollaboratorsActions.updateCollaboratorsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando colaboradores' }),
    );
    const userFirebaseData = yield select((state) => state.users.firebaseData);
    const response = yield call(collaboratorsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(CollaboratorsActions.updateCollaboratorsSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(
                CollaboratorsActions.listCollaboratorsRequest({
                  body: {
                    companyId: userFirebaseData.uid,
                    _includes: 'transportcard,document',
                  },
                }),
              );
            },
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Colaboradores alterados com sucesso',
          }),
        );
        yield put(
          CollaboratorsActions.listCollaboratorsRequest({
            body: {
              companyId: userFirebaseData.uid,
              _includes: 'transportcard,document',
            },
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - updateCollaborators - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.updateCollaboratorsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeList({
  payload,
}: ReturnType<typeof CollaboratorsActions.deleteCollaboratorsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo colaboradores' }),
    );
    const userFirebaseData = yield select((state) => state.users.firebaseData);
    const response = yield call(collaboratorsApi.delete, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(CollaboratorsActions.deleteCollaboratorsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Colaboradores excluídos com sucesso',
        }),
      );
      yield put(
        CollaboratorsActions.listCollaboratorsRequest({
          body: {
            companyId: userFirebaseData.uid,
            _includes: 'transportcard,document',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('collaborators - deleteCollaborators - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CollaboratorsActions.deleteCollaboratorsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const collaboratorsSagas = [
  takeLatest(CollaboratorsTypes.LIST_COLLABORATORS_REQUEST, list),
  takeLatest(CollaboratorsTypes.GET_COLLABORATOR_REQUEST, get),
  takeLatest(CollaboratorsTypes.FILTER_COLLABORATORS_REQUEST, filter),
  takeLatest(CollaboratorsTypes.CREATE_COLLABORATOR_REQUEST, create),
  takeLatest(
    CollaboratorsTypes.LINK_COLLABORATOR_TO_COMPANY_REQUEST,
    linkUserToCompany,
  ),
  takeLatest(
    CollaboratorsTypes.UNLINK_COLLABORATOR_FROM_COMPANY_REQUEST,
    unlinkUserFromCompany,
  ),
  takeLatest(CollaboratorsTypes.UPDATE_COLLABORATOR_REQUEST, update),
  takeLatest(CollaboratorsTypes.DELETE_COLLABORATOR_REQUEST, remove),
  takeLatest(CollaboratorsTypes.CREATE_COLLABORATORS_REQUEST, createList),
  takeLatest(CollaboratorsTypes.UPDATE_COLLABORATORS_REQUEST, updateList),
  takeLatest(CollaboratorsTypes.DELETE_COLLABORATORS_REQUEST, removeList),
];
